import { Box, IconButton } from '@atgfr8/atg-ui-tailwind';
import { CloseIcon, DeleteIcon, ArrowExpandIcon } from '@atgfr8/icons';
import { createSignal, Show } from 'solid-js';

type ImagePageProps = {
  imageUrl: string;
  width: number;
  height: number;
  expandIconPosition?: 'top-right' | 'bottom-left';
  onDelete?: () => void;
  showDelete?: boolean;
};

const ImagePageOverlay = (props: ImagePageProps & { onClose: () => void }) => {
  return (
    <Box
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: '#fff',
        'z-index': 200,
        'box-shadow': '0 0 20px rgba(0,0,0,0.3)',
        'border-radius': '4px',
        overflow: 'hidden',
        padding: '12px',
        'max-height': '95vh',
        'min-width': '95vw',
        display: 'flex',
        'justify-content': 'center',
        'align-items': 'center',
      }}
    >
      <img
        src={props.imageUrl}
        alt="Uploaded Preview"
        style={{
          'max-width': '90vw',
          'max-height': '90vh',
          width: 'auto',
          height: 'auto',
          display: 'block',
          margin: 'auto',
        }}
      />

      <IconButton
        onClick={props.onClose}
        size="sm"
        dataTestId="image-page-overlay-close-button"
        class="absolute top-2 right-2 bg-black text-white hover:bg-[#222]"
      >
        <CloseIcon size={'16px'} />
      </IconButton>
    </Box>
  );
};

export const ImagePage = (props: ImagePageProps) => {
  const [showExpand, setShowExpand] = createSignal(false);
  const [showOverlay, setShowOverlay] = createSignal(false);

  return (
    <>
      <Box
        style={{
          width: `${props.width}px`,
          height: `${props.height}px`,
          position: 'relative',
          overflow: 'hidden',
        }}
        class="[&:hover>.expand-icon]:opacity-100 [&:hover>.expand-icon]:pointer-events-auto"
        onMouseEnter={() => {
          if (props.expandIconPosition !== 'bottom-left') setShowExpand(true);
        }}
        onMouseLeave={() => {
          if (props.expandIconPosition !== 'bottom-left') setShowExpand(false);
        }}
      >
        <img
          src={props.imageUrl}
          alt="Uploaded"
          style={{
            width: '100%',
            height: '100%',
            'object-fit': 'contain',
            display: 'block',
            'border-radius': '4px',
          }}
        />

        {(props.expandIconPosition === 'bottom-left' || showExpand()) && (
          <IconButton
            on:click={(e) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              e.stopPropagation();
              setShowOverlay(true);
            }}
            dataTestId="image-page-expand-button"
            class="expand-icon [&:hover]:bg-black [&:hover]:text-white"
            style={{
              position: 'absolute',
              top: props.expandIconPosition === 'bottom-left' ? 'auto' : '8px',
              bottom:
                props.expandIconPosition === 'bottom-left' ? '8px' : 'auto',
              left: props.expandIconPosition === 'bottom-left' ? '8px' : 'auto',
              right:
                props.expandIconPosition === 'bottom-left' ? 'auto' : '8px',
              'background-color': '#000',
              color: '#fff',
              'box-shadow': '0 2px 5px rgba(0,0,0,0.4)',
              opacity: props.expandIconPosition === 'bottom-left' ? 1 : 0,
              'pointer-events':
                props.expandIconPosition === 'bottom-left' ? 'auto' : 'none',
              transition: 'opacity 0.3s ease',
            }}
          >
            <ArrowExpandIcon size={'16px'} />
          </IconButton>
        )}

        {props.expandIconPosition === 'bottom-left' &&
          props.showDelete !== false && (
            <IconButton
              onClick={() => props.onDelete?.()}
              dataTestId="image-page-delete-button"
              class="[&:hover]:bg-[#b71c1c] [&:hover]:text-white"
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                'background-color': '#d32f2f',
                color: '#fff',
                'box-shadow': '0 2px 5px rgba(0,0,0,0.4)',
                transition: 'opacity 0.3s ease',
              }}
            >
              <DeleteIcon size={'16px'} />
            </IconButton>
          )}
      </Box>

      <Show when={showOverlay()}>
        <ImagePageOverlay {...props} onClose={() => setShowOverlay(false)} />
      </Show>
    </>
  );
};
